import type {
  IconConstant,
} from '@tpg/grammar-web-icons';
import {
  tpgIconAddLabelNumberMap,
  tpgIconCircleShapeCustom,
  tpgIconDelete,
  tpgIconDraw,
  tpgIconFreePolygonCustom,
  tpgIconHouseMapping,
  tpgIconMoveCustom,
  tpgIconPanToolAlt,
  tpgIconPathCustom,
  tpgIconPolygonCustom,
  tpgIconQuestionMark,
  tpgIconRoute,
  tpgIconSquareFoot,
  tpgIconSymbols,
  tpgIconTextFields,
} from '@tpg/grammar-web-icons';
import type { Coordinate } from 'ol/coordinate';
import { transform } from 'ol/proj';

import { DrawToolType } from '@nora-monorepo/nora-api-client';
import type {
  NamedLatLonCoordinate,
} from '@nora-monorepo/shared';
import {
  NoraSupportedCoordinateFormats,
} from '@nora-monorepo/shared';
import { isWebViewMode } from '@nora-monorepo/ui-shared';

// The pixel tolerance used for the webview to increase the touch button tolerance, making them easier to drag on mobile.
const WEBVIEW_HANDLE_PIXEL_TOLERANCE_PX = 22;

/**
 * Calculates the OpenLayers pixel tolerance size for draw tool button handles.
 * Increases the touch area for mobile devices using the webview.
 */
export function getHandleModifyPixelTolerance(): number | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return isWebViewMode() ? WEBVIEW_HANDLE_PIXEL_TOLERANCE_PX : undefined;
}

export function isDrawToolType(value: string | undefined): value is DrawToolType {
  const drawToolTypes: string[] = Object.values(DrawToolType);

  return drawToolTypes.includes(value ?? '');
}

export function olToNamedLatLonCoordinate(
  coordinate: Coordinate,
  name?: string,
): NamedLatLonCoordinate {
  const [longitude, latitude] = transform(
    coordinate,
    NoraSupportedCoordinateFormats.WEB_MERCATOR,
    NoraSupportedCoordinateFormats.WGS84,
  );
  if (latitude === undefined || longitude === undefined) {
    return { latitude: 0, longitude: 0, name };
  }
  return { latitude, longitude, name };
}

export function namedLatLonCoordinateToOlCoordinate(
  coordinate: NamedLatLonCoordinate,
): Coordinate {
  return transform(
    [coordinate.longitude, coordinate.latitude],
    NoraSupportedCoordinateFormats.WGS84,
    NoraSupportedCoordinateFormats.WEB_MERCATOR,
  );
}

export function getIconByType(type: DrawToolType): IconConstant | undefined {
  switch (type) {
    case DrawToolType.tactical_annotation:
      return tpgIconHouseMapping;
    case DrawToolType.circle:
      return tpgIconCircleShapeCustom;
    case DrawToolType.erase:
      return tpgIconDelete;
    case DrawToolType.freehand:
      return tpgIconDraw;
    case DrawToolType.line:
      return tpgIconPathCustom;
    case DrawToolType.move:
      return tpgIconMoveCustom;
    case DrawToolType.polygon:
      return tpgIconFreePolygonCustom;
    case DrawToolType.rectangle:
      return tpgIconPolygonCustom;
    case DrawToolType.route:
      return tpgIconRoute;
    case DrawToolType.ruler:
      return tpgIconSquareFoot;
    case DrawToolType.select:
      return tpgIconPanToolAlt;
    case DrawToolType.symbol:
      return tpgIconSymbols;
    case DrawToolType.spot:
      return tpgIconAddLabelNumberMap;
    case DrawToolType.text:
      return tpgIconTextFields;
    case DrawToolType.building_annotation:
    case DrawToolType.unstyled:
      return tpgIconQuestionMark;
  }
}

export function getDrawToolNameByType(drawToolType: DrawToolType): string {
  return (
    drawToolType.charAt(0).toUpperCase()
    + drawToolType
      .slice(1)
      .replace(/([A-Z])/g, '$1')
      .replace('_', ' ')
  );
}

export function getFeatureNameByDrawToolType(
  drawToolType: DrawToolType,
): string {
  switch (drawToolType) {
    case DrawToolType.line:
      return 'Line';
    case DrawToolType.rectangle:
      return 'Rectangle';
    case DrawToolType.circle:
      return 'Circle';
    case DrawToolType.freehand:
      return 'Freehand Line';
    case DrawToolType.polygon:
      return 'Polygon';
    case DrawToolType.tactical_annotation:
      return 'Tactical Annotation';
    case DrawToolType.spot:
      return 'Spot Map';
    case DrawToolType.ruler:
      return 'Ruler';
    case DrawToolType.route:
      return 'Route';
    case DrawToolType.text:
      return 'Text';
    case DrawToolType.symbol:
    case DrawToolType.select:
    case DrawToolType.erase:
    case DrawToolType.move:
    case DrawToolType.building_annotation:
    case DrawToolType.unstyled:
      return '';
  }
}
